import { Component, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { UserService } from '@app/core/services/user.service';
import { User } from '@app/core/models/user';
import { ScrollConstants } from '@app/core/utils/scroll-constants';
import { Ordering } from '@app/core/utils/ordering';
import { SortableComponent } from '@app/core/sortable-component';
import { PageManager } from '@app/core/utils/page-manager';
import { MessageDialogComponent } from '@app/shared/message-dialog/message-dialog.component';
import { SearchableField } from '@app/shared/search-bar/search-bar.component';
import { SearchCriteria } from '@app/core/utils/search-criteria';
import { SessionService } from '@app/security/session.service';
import { TwoWaySharedService } from '@app/core/services/two-way-shared.service';
import { ControlGroup, ControlTagsService } from '@app/core/services/control-tags.service';
import { Client } from '@app/core/models/client';
import * as _ from 'lodash';
import { forkJoin } from 'rxjs';
import { FeatureFlags } from '@app/core/models/feature-flags';
import { FeatureService } from '@app/core/services/feature.service';
import { Permissions, PermissionService } from '@app/core/services/permission.service';
import { SearchBarComponent } from '../../shared/search-bar/search-bar.component';
import { InfiniteScrollDirective } from 'ngx-infinite-scroll';
import { ColumnSortIconComponent } from '../../shared/column-sort-icon/column-sort-icon.component';
import { CheckboxComponent } from '../../shared/checkbox/checkbox.component';
import { SpinnerComponent } from '../../shared/spinner/spinner.component';
import { UserResetComponent } from '../user-reset/user-reset.component';
import { MessageDialogComponent as MessageDialogComponent_1 } from '../../shared/message-dialog/message-dialog.component';
import { DatePipe } from '@angular/common';
import { InputFieldPipe } from '../../shared/pipes/input-field.pipe';

@Component({
    selector: 'app-user-list',
    templateUrl: './user-list.component.html',
    styleUrls: ['./user-list.component.scss'],
    standalone: true,
    imports: [SearchBarComponent, InfiniteScrollDirective, ColumnSortIconComponent, CheckboxComponent, SpinnerComponent, UserResetComponent, MessageDialogComponent_1, DatePipe, InputFieldPipe]
})
export class UserListComponent extends SortableComponent implements OnInit {
  @ViewChild(MessageDialogComponent, { static: true })
  messageDialog: MessageDialogComponent;
  @Input() fixedHeader: boolean = false;
  @Output() editUser: EventEmitter<string> = new EventEmitter();
  users: User[] = [];
  sso: boolean = false;
  loaded = false;
  readonly limit = 20;
  readonly throttle = ScrollConstants.throttle;
  readonly scrollDistance = ScrollConstants.scrollDistance;
  offset = 0;
  ordering: Ordering;
  searchCriteria: SearchCriteria;
  showModal: boolean = false;
  searchableFields = [
    new SearchableField('Name', 'full_name'),
    new SearchableField('Email', 'email_address'),
    new SearchableField('Role', 'role_id'),
  ];
  pageManager: PageManager = new PageManager();
  currentUserId: string;
  currentClient: Client;
  featureFlags = FeatureFlags;
  canEditUser = false;
  groups: ControlGroup[];

  constructor(
    private userService: UserService,
    private sessionService: SessionService,
    public controlTagsService: ControlTagsService,
    private featureService: FeatureService,
    public twoWayService: TwoWaySharedService,
    public permissionService: PermissionService,
  ) {
    super();
  }

  ngOnInit() {
    this.currentUserId = this.sessionService.currentUser.id;
    this.currentClient = this.sessionService.getCurrentUsersClient();
    this.reloadUsers();
    this.canEditUser =
    this.permissionService.checkPermissions(Permissions.user_view) &&
    this.permissionService.checkPermissions(Permissions.user_edit);
    this.sso = !!this.sessionService.currentUser.client.sso_config?.role_id_value_map[0];
    if (this.twoWayService.showTwoWay()) {
      this.getControlGroups();
    }
  }

  get hideControlGroup(): boolean {
    return this.featureService.checkFlag(this.featureFlags.hide_control_group);
  }

  deleteUsersConfig() {
    const selectedUsers = this.users.filter((u) => u.selected);
    if (selectedUsers.length === 1) {
      return {
        title: 'Delete User',
        icon: 'icon-trash',
        msg: `Are you sure you want to delete ${selectedUsers[0].email_address}? This cannot be undone.`,
        btns: [
          { name: 'Cancel', icon: 'fa-times' },
          { name: 'Delete user', class: 'btn btn-warning', icon: 'fa-trash' },
        ],
      };
    }
    return {
      title: 'Delete Users',
      icon: 'icon-trash',
      msg: `Are you sure you want to delete ${selectedUsers.length} users? This cannot be undone.`,
      btns: [
        { name: 'Cancel', icon: 'fa-times' },
        { name: 'Delete users', class: 'btn btn-warning', icon: 'fa-trash' },
      ],
    };
  }

  openModal() {
    if (!!this.users.find((u) => u.selected)) {
      this.showModal = true;
    } else {
      this.messageDialog.showMessage('No users selected.');
    }
  }

  closeModal(decision) {
    this.showModal = false;
    if (decision === 'Delete user' || decision === 'Delete users') {
      this.deleteUsers();
    }
  }

  onRequestSearch(searchCriteria: SearchCriteria) {
    this.searchCriteria = searchCriteria;
    if (this.searchCriteria.searchFields.length === 0) {
      this.pageManager = new PageManager();
      this.users = [];
      this.offset = 0;
    } else {
      this.reloadUsers();
    }
  }

  onClearSearch() {
    this.searchCriteria = undefined;
    this.reloadUsers();
  }

  reloadUsers() {
    this.loaded = false;
    this.pageManager = new PageManager();
    this.users = [];
    this.offset = 0;
    this.getUsers();
  }

  selectUser(user, isSelected) {
    user.selected = isSelected;
  }

  editUserClicked(id: string) {
    this.editUser.emit(id);
  }

  deleteUsers() {
    const deleteUser = this.users.find((u) => u.selected);
    const feedEnabled = this.sessionService.currentUser.client.feed_enabled;
    const twoWayEnabled =
      this.sessionService.currentUser.client.two_way_config.enabled;
    if (deleteUser) {
      if (feedEnabled && twoWayEnabled) {
        forkJoin([
          this.userService.deletePortalUser(deleteUser.id),
          this.userService.unassignPortalAgent(deleteUser.id),
        ]).subscribe(
          (responses) => {
            this.pageManager.forEachPage((p) => {
              const index = p.findIndex((u: User) => u.id === deleteUser.id);
              if (index >= 0) {
                p.splice(index, 1);
              }
            });
            this.users = this.pageManager.flattenPages();
            this.deleteUsers();
          },
          (e) => {
            this.messageDialog.showMessage(
              this.getErrorMessage(e, 'Something went wrong. please try again'),
            );
          },
        );
      } else {
        this.userService.deletePortalUser(deleteUser.id).subscribe(
          (r) => {
            this.pageManager.forEachPage((p) => {
              const index = p.findIndex((u: User) => u.id === deleteUser.id);
              if (index >= 0) {
                p.splice(index, 1);
              }
            });
            this.users = this.pageManager.flattenPages();
            this.deleteUsers();
          },
          (e) => {
            this.messageDialog.showMessage(
              this.getErrorMessage(e, 'Something went wrong. please try again'),
            );
          },
        );
      }
    }
  }

  // The delete-user API responds with a real `message` (e.g. blocked because
  // the user owns Relay Insights Studio reports) on 409/503 failures. Angular's
  // HttpErrorResponse wraps the parsed JSON body under `.error`, so check there
  // first.
  //
  // Deliberately NOT falling back to a bare HttpErrorResponse's own `.message`:
  // Angular auto-generates that as a technical string like "Http failure
  // response for https://.../client/123/users/456: 0 Unknown Error" whenever
  // there's no parseable JSON body (network failure, CORS block, non-JSON
  // error page, etc) — showing that verbatim would leak the internal API
  // host/path into the user-facing dialog. `.message` is only trusted for
  // non-HTTP errors (unexpected JS exceptions), where no such leak exists.
  private getErrorMessage(error: unknown, fallback: string): string {
    if (
      error &&
      typeof error === 'object' &&
      'error' in error &&
      error.error &&
      typeof error.error === 'object' &&
      'message' in error.error &&
      typeof error.error.message === 'string'
    ) {
      return error.error.message;
    }
    if (
      !(error instanceof HttpErrorResponse) &&
      error &&
      typeof error === 'object' &&
      'message' in error &&
      typeof error.message === 'string'
    ) {
      return error.message;
    }
    return fallback;
  }

  getUsers() {
    const offset = this.offset;
    this.userService
      .getUsers(
        this.limit,
        this.offset,
        this.searchCriteria,
        this.ordering,
        this.offset !== 0,
      )
      .subscribe(
        (users) => {
          this.pageManager.addPage(offset, users);
          this.users = this.pageManager.flattenPages();
        },
        (error) => {
          this.messageDialog.showMessage('Ooops...Could not load user list.');
        },
        () => {
          this.loaded = true;
        },
      );
  }

  onScrollDown() {
    this.offset += this.limit;
    this.getUsers();
  }

  fetchData() {
    this.pageManager = new PageManager();
    this.users = [];
    this.loaded = false;
    this.offset = 0;
    this.getUsers();
  }

  getControlGroups(): void {
    this.controlTagsService.getGroups().subscribe(
      (groups) => {
        this.groups = groups;
      },
      (error) => {
        this.messageDialog.showMessage(
          'Ooops...There was a problem loading group data.  Groups for some users may be missing.  Please refresh to try again.',
        );
      },
    );
  }

  getGroupForUser(user): string {
    const matchingGroup = _.find(this.groups, (group) => {
      return group.group_id === user.group_id;
    });

    return matchingGroup ? matchingGroup.group_name : '';
  }
}
